import { HasTags } from './tag';
import { Equipment, EquipmentData } from './equipment';
import { flatMap } from 'lodash';
import { AlarmPriority } from './alarm';

export interface Site extends HasTags {
  fullAddress?: string;
  id: number;
  name: string;
  locationGeoJson: string;
  timeZone: string;
  ianaTimeZone: string;
  imageFileLink: string;
  postalCode: string;
  utilityId: number;
  isMVoult: boolean;
  // site schematic for op summary
  singleLineImageFileLink?: string;
  customerName: string;
  nodes: SiteNode[];
  webPortalLayoutJson: SiteLayoutConfig;
  customerId: number;
  commissionedOn?: string;
  address: string;
  city: string;
  state: string;
  stateOrProvince: string;
  utilityName?: string;
  installerCompanyName?: string;
  dataPartnerId?: string;
  endUserName?: string;
  isOnline: boolean | null;
}

export interface SiteNode extends HasTags {
  id: number;
  name: string;
  imageFileLink: string;
  locationGeoJson: string;
  equipment: Equipment[];
}

export interface SiteKPI {
  netLoad: number;
  load: number;
  storageStatus: 'charging' | 'discharging' | 'idle' | 'alarm' | 'fault';
  storageDischarge: number;
  powerOutput: number;
  dailyEnergyGeneration: number;
}

export enum EnrollmentStatus {
  Ineligible = 'Not Eligible',
  Eligible = 'Eligible',
  Pending = 'Pending',
  Enrolled = 'Enrolled',
  Error = 'Error',
  Unenrolled = 'Unenrolled',
  OptOut = 'Opted Out',
  OptIn = 'Opted In',
  NonResponsive = 'Non-Responsive',
}

export interface SiteSummary {
  siteId: number;
  name: string;
  customerId: number;
  customerName: string;
  fullAddress: string;
  zipCode: string;
  utilityId?: number;
  utilityName?: string;
  totalPower: number;
  currentKPIs: SiteKPI;
  activeAlarmCount: number;
  highestActiveAlarmStatus?: AlarmPriority;
  highestActiveAlarmCount?: number;
  installationDate: string;
  gridServicesEnrollmentStatus: keyof typeof EnrollmentStatus;
  dataPartnerSiteId?: string;
  installerCompanyName?: string;
  endUserName?: string;
  onlineStatus?: boolean;
  signalStrength?: number;
  equipment?: EquipmentData[];
  isOnline: boolean;
}

export const getSiteEquipment = (site: Site) => {
  return flatMap(site.nodes.map((node) => node.equipment));
};

export const getSiteTags = (site: Site) => {
  let allTags = site.tags;
  site.nodes.forEach((node) => {
    allTags = allTags.concat(node.tags);
  });
  getSiteEquipment(site).forEach((equipment) => {
    allTags = allTags.concat(equipment.tags);
  });
  return allTags;
};

export const getSiteEquipmentById = (site: Site, equipmentId: number) => {
  const allEquipment = getSiteEquipment(site);
  return allEquipment.find((equipment) => equipment.id === equipmentId);
};

export const getSiteTagById = (site: Site, tagId: string) => {
  const allTags = getSiteTags(site);
  return allTags.find((tag) => tag.id === tagId);
};

export interface SiteLayoutConfig {
  viewSettings?: {
    hideControlSummary?: boolean;
    hideDashboard?: boolean;
  };
  operationalSummary?: {
    hideRateChart?: boolean;
  };
  lobby?: {
    powerFlow?: {
      elementAttributes: {
        [key: string]: string[];
      };
    };
    kPIs?: {
      elements: KPIElement[];
    };
  };
}

/*
 * Possible keys for lobby.powerFlow.elementAttributes
 */
export enum ComponentId {
  Generator = 'Generator',
  Solar = 'Solar',
  Utility = 'Utility',
  Storage = 'Storage',
  Load = 'Load',
  Inverter = 'Inverter',
  PvInverter = 'PvInverter',
  EVCharger = 'EVCharger',
}

export const SocComponentId = 'SOC';

interface KPIElement {
  title: string;
  subtitle: string;
  attributes: string[];
}

export enum AttrType {
  Current = 'current',
  Historical = 'historical',
  ExtraAttributes = 'extraAttributes',
}

export const tagAttributes = {
  solar: {
    current: [['current', 'lobby', 'pvdc']],
    historical: [['historical', 'lobby', 'pvdc']],
  },
  generator: {
    current: [['current', 'lobby', 'base generation']],
    historical: [
      ['historical', 'lobby', 'state of charge'],
      ['historical', 'lobby', 'base generation'],
    ],
  },
  pvinverter: {
    current: [['current', 'lobby', 'pvac']],
    historical: [['historical', 'lobby', 'pvac']],
  },
  utility: {
    current: [['current', 'lobby', 'utility']],
    historical: [['historical', 'lobby', 'utility']],
    extraAttributes: [
      ['lobby', 'utility_status'],
      ['lobby', 'utility_hz'],
      ['lobby', 'utility_vac'],
    ],
  },
  storage: {
    current: [['current', 'lobby', 'storage']],
    historical: [
      ['historical', 'lobby', 'storage'],
      ['historical', 'lobby', 'state of charge'],
    ],
    extraAttributes: [
      ['lobby', 'storage_v'],
      ['lobby', 'state of charge'],
    ],
  },
  load: {
    current: [['current', 'lobby', 'load']],
    historical: [['historical', 'lobby', 'load']],
  },
  evcharger: {
    current: [['current', 'lobby', 'evcharger']],
    historical: [['historical', 'lobby', 'evcharger']],
  },
  inverter: {
    current: [['current', 'lobby', 'inverters']],
    historical: [['historical', 'lobby', 'inverters']],
  },
};
